/**
 * Sports Betting Service
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import sportsApiService from './sportsApiService';
import apiService from './apiService';
import { invalidateBalanceQueries } from './moneyService';
import {
  Sport,
  Location,
  League,
  SportsEvent,
  BettingHistoryItem,
  SportsApiResponse,
  EventsApiResponse,
  GroupedEventsApiResponse,
  PlaceBetRequest,
  PlaceBetResponse,
} from '../types/sports';

// ============================================
// API Functions
// ============================================

// Get all sports categories
export async function getSports(params: {
  sportType?: 'sport' | 'esport';
} = {}): Promise<Sport[]> {
  try {
    const response = await sportsApiService.postRaw<SportsApiResponse<Sport[]>>(
      '/api/Sports/getSports',
      { sportType: params.sportType }
    );
    
    if (!response) {
      console.warn('[SportsService] getSports: No response from server');
      return [];
    }
    
    if (response.status !== 0) {
      const errorMsg = response?.message?.warning || 'Failed to get sports';
      console.warn('[SportsService] getSports error:', errorMsg);
      return []; // Return empty array instead of throwing
    }
    
    return response.data || [];
  } catch (error) {
    console.error('[SportsService] getSports error:', error);
    return []; // Return empty array instead of throwing
  }
}

// Get all locations
export async function getLocations(): Promise<Location[]> {
  try {
    const response = await sportsApiService.postRaw<SportsApiResponse<Location[]>>(
      '/api/Sports/getLocations',
      {}
    );
    
    if (!response) {
      console.warn('[SportsService] getLocations: No response from server');
      return [];
    }
    
    if (response?.status !== 0) {
      const errorMsg = response?.message?.warning || 'Failed to get locations';
      console.warn('[SportsService] getLocations error:', errorMsg);
      return []; // Return empty array instead of throwing
    }
    
    return response.data || [];
  } catch (error) {
    console.error('[SportsService] getLocations error:', error);
    return []; // Return empty array instead of throwing
  }
}

// Get leagues with optional filters
export async function getLeagues(params: {
  sportId?: number | string;
  locationId?: number;
  sportType?: 'sport' | 'esport';
} = {}): Promise<League[]> {
  try {
    const response = await sportsApiService.postRaw<SportsApiResponse<League[]>>(
      '/api/Sports/getLeagues',
      params
    );
    
    if (!response) {
      console.warn('[SportsService] getLeagues: No response from server');
      return [];
    }
    
    if (response?.status !== 0) {
      const errorMsg = response?.message?.warning || 'Failed to get leagues';
      console.warn('[SportsService] getLeagues error:', errorMsg);
      return []; // Return empty array instead of throwing
    }
    
    return response.data || [];
  } catch (error) {
    console.error('[SportsService] getLeagues error:', error);
    return []; // Return empty array instead of throwing
  }
}

// Get markets
export async function getMarkets(): Promise<any[]> {
  try {
    const response = await sportsApiService.postRaw<SportsApiResponse<any[]>>(
      '/api/Sports/getMarkets',
      {}
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get markets');
    }
    return response.data || [];
  } catch (error) {
    console.error('[SportsService] getMarkets error:', error);
    throw error;
  }
}

// Get prematch events (grouped by location and league)
export async function getPrematchEvents(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  sportType?: 'sport' | 'esport';
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  grouped?: boolean;
} = {}): Promise<GroupedEventsApiResponse> {
  try {
    const response = await sportsApiService.postRaw<GroupedEventsApiResponse>(
      '/api/Sports/getPrematchEvents',
      {
        sportId: params.sportId,
        locationId: params.locationId,
        leagueId: params.leagueId,
        sportType: params.sportType,
        page: params.page || 1,
        perPage: params.perPage || 50,
        sortBy: params.sortBy || 'startDate',
        sortOrder: params.sortOrder || 'asc',
        grouped: params.grouped !== false, // Default to true
      }
    );
    
    // Handle response validation
    if (!response) {
      throw new Error('No response from server');
    }
    
    if (response.status !== 0) {
      const errorMsg = response?.message?.warning || 'Failed to get events';
      throw new Error(errorMsg);
    }
    
    // Ensure groupedEvents exists (default to empty array)
    if (params.grouped !== false && !response.groupedEvents) {
      return {
        ...response,
        groupedEvents: [],
        pagination: response.pagination || {
          page: params.page || 1,
          perPage: params.perPage || 50,
          totalPages: 0,
          totalCount: 0
        }
      };
    }
    
    return response;
  } catch (error: any) {
    console.error('[SportsService] getPrematchEvents error:', error);
    
    // Return empty response structure on error instead of throwing
    // This allows the UI to show "no events" instead of error state
    if (params.grouped !== false) {
      return {
        status: 1,
        groupedEvents: [],
        pagination: {
          page: params.page || 1,
          perPage: params.perPage || 50,
          totalPages: 0,
          totalCount: 0
        },
        message: {
          warning: error?.message || 'Failed to get events',
          success: undefined
        }
      };
    }
    
    throw error;
  }
}

// Get prematch events (flat list - for backwards compatibility)
export async function getPrematchEventsFlat(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}): Promise<EventsApiResponse> {
  try {
    const response = await sportsApiService.postRaw<EventsApiResponse>(
      '/api/Sports/getPrematchEvents',
      {
        sportId: params.sportId,
        locationId: params.locationId,
        leagueId: params.leagueId,
        page: params.page || 1,
        perPage: params.perPage || 50,
        sortBy: params.sortBy || 'startDate',
        sortOrder: params.sortOrder || 'asc',
        grouped: false,
      }
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get events');
    }
    return response;
  } catch (error) {
    console.error('[SportsService] getPrematchEventsFlat error:', error);
    throw error;
  }
}

// Get live events (grouped by location and league)
export async function getLiveEvents(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  sportType?: 'sport' | 'esport';
  page?: number;
  perPage?: number;
  grouped?: boolean;
} = {}): Promise<GroupedEventsApiResponse> {
  try {
    const response = await sportsApiService.postRaw<GroupedEventsApiResponse>(
      '/api/Sports/getLiveEvents',
      {
        sportId: params.sportId,
        locationId: params.locationId,
        leagueId: params.leagueId,
        sportType: params.sportType,
        page: params.page || 1,
        perPage: params.perPage || 50,
        grouped: params.grouped !== false, // Default to true
      }
    );
    
    if (!response) {
      throw new Error('No response from server');
    }
    
    if (response?.status !== 0) {
      const errorMsg = response?.message?.warning || 'Failed to get live events';
      throw new Error(errorMsg);
    }
    
    // Ensure groupedEvents exists (default to empty array)
    if (params.grouped !== false && !response.groupedEvents) {
      return {
        ...response,
        groupedEvents: [],
        pagination: response.pagination || {
          page: params.page || 1,
          perPage: params.perPage || 50,
          totalPages: 0,
          totalCount: 0
        }
      };
    }
    
    return response;
  } catch (error: any) {
    console.error('[SportsService] getLiveEvents error:', error);
    
    // Return empty response structure on error instead of throwing
    if (params.grouped !== false) {
      return {
        status: 1,
        groupedEvents: [],
        pagination: {
          page: params.page || 1,
          perPage: params.perPage || 50,
          totalPages: 0,
          totalCount: 0
        },
        message: {
          warning: error?.message || 'Failed to get live events',
          success: undefined
        }
      };
    }
    
    throw error;
  }
}

// Get live events (flat list - for backwards compatibility)
export async function getLiveEventsFlat(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  page?: number;
  perPage?: number;
} = {}): Promise<EventsApiResponse> {
  try {
    const response = await sportsApiService.postRaw<EventsApiResponse>(
      '/api/Sports/getLiveEvents',
      {
        sportId: params.sportId,
        locationId: params.locationId,
        leagueId: params.leagueId,
        page: params.page || 1,
        perPage: params.perPage || 50,
        grouped: false,
      }
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get live events');
    }
    return response;
  } catch (error) {
    console.error('[SportsService] getLiveEventsFlat error:', error);
    throw error;
  }
}

// Get event detail
export async function getEventDetail(params: {
  fixtureId: string;
  gameType?: 'prematch' | 'live';
}): Promise<SportsEvent | null> {
  try {
    const response = await sportsApiService.postRaw<{ status: number; event: SportsEvent; message: any }>(
      '/api/Sports/getEventDetail',
      params
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get event detail');
    }
    return response.event || null;
  } catch (error) {
    console.error('[SportsService] getEventDetail error:', error);
    throw error;
  }
}

// Get live score board URL
export async function getLiveScoreBoard(fixtureId: string): Promise<{ url: string } | null> {
  try {
    const response = await sportsApiService.postRaw<SportsApiResponse<{ url: string }>>(
      '/api/Sports/getLiveScoreBoard',
      { fixtureId }
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get live score board');
    }
    return response.data || null;
  } catch (error) {
    console.error('[SportsService] getLiveScoreBoard error:', error);
    throw error;
  }
}

// Get sports risk settings (odds delay + UI constraints)
export async function getSportsRiskSettings(): Promise<{ oddsDelaySeconds: number; minSelectableOdds: number }> {
  try {
    const response = await sportsApiService.postRaw<{ status: number; oddsDelaySeconds: number; minSelectableOdds?: number; message: any }>(
      '/api/Sports/getSportsRiskSettings',
      {}
    );
    if (response?.status !== 0) {
      return { oddsDelaySeconds: 0, minSelectableOdds: 1.35 };
    }
    return {
      oddsDelaySeconds: response.oddsDelaySeconds ?? 0,
      minSelectableOdds: typeof response.minSelectableOdds === 'number' ? response.minSelectableOdds : 1.35,
    };
  } catch (error) {
    console.warn('[SportsService] getSportsRiskSettings error:', error);
    return { oddsDelaySeconds: 0, minSelectableOdds: 1.35 };
  }
}

// Place a bet
export async function placeBet(params: PlaceBetRequest): Promise<PlaceBetResponse> {
  try {
    const response = await sportsApiService.postRaw<PlaceBetResponse>(
      '/api/Sports/placeBet',
      params
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to place bet');
    }
    return response;
  } catch (error) {
    console.error('[SportsService] placeBet error:', error);
    throw error;
  }
}

// Get betting history
export async function getBettingHistory(params: {
  sportType?: 'sport' | 'esport';
  page?: number;
  perPage?: number;
  status?: string;
} = {}): Promise<{
  bets: BettingHistoryItem[];
  pagination: { page: number; perPage: number; totalPages: number; totalCount: number };
}> {
  try {
    const response = await sportsApiService.postRaw<any>(
      '/api/Sports/getBettingHistory',
      {
        page: params.page || 1,
        perPage: params.perPage || 20,
        status: params.status,
        sportType: params.sportType,
      }
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to get betting history');
    }
    return {
      bets: response.bets || [],
      pagination: response.pagination || { page: 1, perPage: 20, totalPages: 1, totalCount: 0 },
    };
  } catch (error) {
    console.error('[SportsService] getBettingHistory error:', error);
    throw error;
  }
}

// Cancel a bet
export async function cancelBet(betId: string): Promise<{ newBalance: number }> {
  try {
    const response = await sportsApiService.postRaw<any>(
      '/api/Sports/cancelBet',
      { betId }
    );
    if (response?.status !== 0) {
      throw new Error(response?.message?.warning || 'Failed to cancel bet');
    }
    return { newBalance: response.newBalance };
  } catch (error) {
    console.error('[SportsService] cancelBet error:', error);
    throw error;
  }
}

// ============================================
// React Query Hooks
// ============================================

// Hook for getting sports
export function useSports(params: { sportType?: 'sport' | 'esport' } = {}) {
  return useQuery({
    queryKey: ['sports', 'list', params.sportType],
    queryFn: () => getSports(params),
    // Avoid firing requests on pages where Sidebar mounts but sportType isn't active.
    enabled: params.sportType != null,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    // Return empty array on error to prevent UI breakage
    throwOnError: false,
    placeholderData: [],
  });
}

// Hook for getting locations
export function useLocations() {
  return useQuery({
    queryKey: ['sports', 'locations'],
    queryFn: getLocations,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    // Return empty array on error to prevent UI breakage
    throwOnError: false,
    placeholderData: [],
  });
}

// Hook for getting leagues
export function useLeagues(params: { sportId?: number | string; locationId?: number; sportType?: 'sport' | 'esport' } = {}) {
  return useQuery({
    queryKey: ['sports', 'leagues', params],
    queryFn: () => getLeagues(params),
    enabled: params.sportType != null,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    // Return empty array on error to prevent UI breakage
    throwOnError: false,
    placeholderData: [],
  });
}

// Hook for getting markets
export function useMarkets() {
  return useQuery({
    queryKey: ['sports', 'markets'],
    queryFn: getMarkets,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
  });
}

// Hook for getting prematch events
export function usePrematchEvents(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  sportType?: 'sport' | 'esport';
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}) {
  return useQuery({
    queryKey: ['sports', 'prematch', params],
    queryFn: () => getPrematchEvents(params),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    // Return default empty structure on error to prevent UI breakage
    throwOnError: false,
    // Provide default value
    placeholderData: (previousData) => previousData || {
      status: 0,
      groupedEvents: [],
      pagination: {
        page: params.page || 1,
        perPage: params.perPage || 50,
        totalPages: 0,
        totalCount: 0
      },
      message: {
        success: undefined,
        warning: undefined
      }
    },
  });
}

// Hook for getting live events
export function useLiveEvents(params: {
  sportId?: number | string;
  locationId?: number;
  leagueId?: string;
  sportType?: 'sport' | 'esport';
  page?: number;
  perPage?: number;
} = {}) {
  return useQuery({
    queryKey: ['sports', 'live', params],
    queryFn: () => getLiveEvents(params),
    staleTime: 5 * 1000, // 5 seconds for live
    gcTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Always refetch on mount
    retry: 2,
    retryDelay: 1000,
    // Return default empty structure on error to prevent UI breakage
    throwOnError: false,
    // Provide default value
    placeholderData: (previousData) => previousData || {
      status: 0,
      groupedEvents: [],
      pagination: {
        page: params.page || 1,
        perPage: params.perPage || 50,
        totalPages: 0,
        totalCount: 0
      },
      message: {
        success: undefined,
        warning: undefined
      }
    },
  });
}

// Hook for getting event detail
export function useEventDetail(fixtureId: string, gameType: 'prematch' | 'live' = 'prematch') {
  return useQuery({
    queryKey: ['sports', 'event', fixtureId, gameType],
    queryFn: () => getEventDetail({ fixtureId, gameType }),
    enabled: !!fixtureId,
    staleTime: gameType === 'live' ? 5 * 1000 : 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: gameType === 'live' ? 5 * 1000 : 30 * 1000,
    retry: 3,
  });
}

// Hook for sports risk settings (odds delay) - used by bet slip for countdown
export function useSportsRiskSettings(enabled: boolean = true) {
  return useQuery({
    queryKey: ['sports', 'riskSettings'],
    queryFn: getSportsRiskSettings,
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// Hook for placing bet
export function usePlaceBet() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: placeBet,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// Hook for getting betting history
export function useBettingHistory(params: {
  page?: number;
  perPage?: number;
  status?: string;
  sportType?: 'sport' | 'esport';
} = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: ['sports', 'bettingHistory', params],
    queryFn: () => getBettingHistory(params),
    enabled: enabled && apiService.isAuthenticated(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 3,
  });
}

// Hook for cancelling bet
export function useCancelBet() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: cancelBet,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// ============================================
// Utility Functions
// ============================================

// Format odds display
export function formatOdds(odds: number, format: 'decimal' | 'american' | 'fractional' = 'decimal'): string {
  if (!odds) return '-';
  
  switch (format) {
    case 'american':
      if (odds >= 2) {
        return `+${Math.round((odds - 1) * 100)}`;
      } else {
        return `${Math.round(-100 / (odds - 1))}`;
      }
    case 'fractional':
      const decimal = odds - 1;
      const numerator = Math.round(decimal * 100);
      const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
      const divisor = gcd(numerator, 100);
      return `${numerator / divisor}/${100 / divisor}`;
    case 'decimal':
    default:
      return odds.toFixed(2);
  }
}

// Calculate potential win
export function calculatePotentialWin(stake: number, odds: number): number {
  return stake * odds;
}

// Calculate profit
export function calculateProfit(stake: number, odds: number): number {
  return (stake * odds) - stake;
}

// Group events by league
export function groupEventsByLeague(events: SportsEvent[]): Map<string, SportsEvent[]> {
  const grouped = new Map<string, SportsEvent[]>();
  
  events.forEach(event => {
    const key = event.league.id;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(event);
  });
  
  return grouped;
}

// Group events by sport
export function groupEventsBySport(events: SportsEvent[]): Map<number, SportsEvent[]> {
  const grouped = new Map<number, SportsEvent[]>();
  
  events.forEach(event => {
    const key = event.sport.oddsId;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(event);
  });
  
  return grouped;
}

function parseUtcDate(dateStr: string): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const s = dateStr.trim();
  const iso = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function formatEventTime(dateStr: string): string {
  const date = parseUtcDate(dateStr);
  if (!date) return '';
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  if (diff < 0) {
    return 'Started';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

export function getTimeUntilEvent(dateStr: string): { days: number; hours: number; minutes: number; seconds: number } {
  const date = parseUtcDate(dateStr);
  if (!date) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const now = new Date();
  const diff = Math.max(0, date.getTime() - now.getTime());
  
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

const sportsService = {
  getSports,
  getLocations,
  getLeagues,
  getMarkets,
  getPrematchEvents,
  getLiveEvents,
  getEventDetail,
  getLiveScoreBoard,
  getSportsRiskSettings,
  placeBet,
  getBettingHistory,
  cancelBet,
  formatOdds,
  calculatePotentialWin,
  calculateProfit,
  groupEventsByLeague,
  groupEventsBySport,
  formatEventTime,
  getTimeUntilEvent,
};

export default sportsService;
